/**
 * ComponenteLayout - Layout Principal da Aplicação
 * 
 * CONTEXTO DE NEGÓCIO:
 * Componente de layout que envolve todas as páginas da aplicação.
 * Define a estrutura visual consistente: Header + Conteúdo + Footer.
 * 
 * FUNCIONALIDADES:
 * - Estruturar layout com cabeçalho, conteúdo principal e rodapé
 * - Renderizar componentes filhos (páginas) no slot de conteúdo
 * - Suporta nested routes via React Router Outlet
 * - Garantir altura mínima de viewport
 * 
 * RESPONSABILIDADES:
 * - Compor cabeçalho, conteúdo e rodapé
 * - Fornecer container consistente para todas as páginas
 * - Gerenciar espaçamento e estrutura visual global
 */

import { ReactNode } from 'react';
import { Outlet } from 'react-router-dom';
import { ComponenteCabecalho } from './ComponenteCabecalho';
import { ComponenteRodape } from './ComponenteRodape';

/**
 * Interface de propriedades do ComponenteLayout
 */
interface PropriedadesComponenteLayout {
  /**
   * Conteúdo filho a ser renderizado na área principal
   * (páginas da aplicação). Se não fornecido, usa Outlet
   * para suportar nested routes.
   */
  children?: ReactNode;
}

/**
 * Componente de layout principal
 * 
 * IMPLEMENTAÇÃO:
 * - Usa Flexbox para layout vertical
 * - Cabeçalho fixo no topo
 * - Conteúdo principal com flex-grow
 * - Rodapé sempre no final (mt-auto)
 * - Suporta nested routes com Outlet (quando usado sem children)
 * 
 * ESTRUTURA:
 * ```
 * <div> (flex column, min-h-screen)
 *   <ComponenteCabecalho />
 *   <main> (flex-grow)
 *     {children ou <Outlet />}
 *   </main>
 *   <ComponenteRodape />
 * </div>
 * ```
 */
export function ComponenteLayout(
  props: PropriedadesComponenteLayout
): JSX.Element {
  return (
    <div className="flex min-h-screen flex-col bg-gray-50">
      {/* Cabeçalho */}
      <ComponenteCabecalho />

      {/* Área de Conteúdo Principal */}
      <main className="grow">
        <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
          {/* Se children fornecido, usa ele; senão usa Outlet para nested routes */}
          {props.children ?? <Outlet />}
        </div>
      </main>

      {/* Rodapé */}
      <ComponenteRodape />
    </div>
  );
}
